import React, { ReactNode, useRef } from 'react';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';

// Reusable styled components for fieldset dropdown
const StyledFieldsetContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '250px',
  [theme.breakpoints.down(1520)]: {
    width: '200px',
  },
}));

const StyledFieldset = styled('fieldset')(({ theme }) => ({
  border: '1px solid #C4C4C4',
  borderRadius: '8px',
  padding: '0',
  margin: '0',
  position: 'relative',
  height: '40px', // Fixed height to match button
  '&:hover': {
    borderColor: '#999999',
    '& legend': {
      color: '#333333', // Darker legend text on hover
    },
  },
}));

const StyledLegend = styled('legend')(({ theme }) => ({
  fontSize: '12px',
  color: '#666666',
  padding: '0 8px',
  marginLeft: '8px',
  fontWeight: 400,
  position: 'absolute',
  top: '-8px', // Position legend outside the fieldset boundary
  backgroundColor: 'white', // Add background to cover border line
  zIndex: 1, // Ensure legend stays above other elements
  [theme.breakpoints.down(1520)]: {
    fontSize: '11px',
  },
}));

const StyledFieldsetButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '40px', // Fixed height
  borderRadius: '7px',
  border: 'none',
  backgroundColor: 'transparent',
  color: '#000000',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 400,
  justifyContent: 'flex-start',
  padding: '0 14px 0px 14px', // Only horizontal and bottom padding
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '& span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'left',
  },
  '&:hover': {
    backgroundColor: 'rgba(245, 245, 245, 0.7)', // Lighter hover background
    borderRadius: '7px',
  },
  '&.Mui-disabled': {
    backgroundColor: '#F5F5F5',
    color: '#999999',
  },
  [theme.breakpoints.down(1520)]: {
    fontSize: '14px',
  },
}));

interface FieldsetDropdownProps {
  legend: string;
  displayText: string;
  isMenuVisible: boolean;
  onToggle: (event?: React.MouseEvent) => void;
  disabled?: boolean;
  showLegend?: boolean;
  children: ReactNode; // The custom menu component
  className?: string;
}

export const FieldsetDropdown: React.FC<FieldsetDropdownProps> = ({
  legend,
  displayText,
  isMenuVisible,
  onToggle,
  disabled = false,
  showLegend = true,
  children,
  className = '',
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className={`relative ${className}`}>
      <StyledFieldsetContainer>
        <StyledFieldset>
          {showLegend && <StyledLegend>{legend}</StyledLegend>}
          <StyledFieldsetButton ref={buttonRef} onClick={onToggle} disabled={disabled}>
            <span className={displayText.includes('Billed Entity') || displayText.includes('Transaction Date') ? 'text-neutral-60' : ''}>{displayText}</span>
            <svg
              className='ml-auto w-16 h-16'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              style={{
                transform: isMenuVisible ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            >
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
            </svg>
          </StyledFieldsetButton>
        </StyledFieldset>
      </StyledFieldsetContainer>

      {/* Pass the buttonRef to children through React.cloneElement */}
      {React.isValidElement(children) &&
        React.cloneElement(children as React.ReactElement<any>, {
          toggleButtonRef: buttonRef,
        })}
    </div>
  );
};
